import {
  BaseState,
  IAccountHolderDetails,
  ICheckout,
  IKairosKashPaymentResponseData,
  IPaginate,
  IPagination,
  IPayments,
  IPaymentStatusIndicatorResponse,
  IResponse,
} from '@/types/types';
import { isNetworkError } from '@/utils/helpers';
import { brandName } from '@/utils/brand';
import { Module, MutationTree, ActionTree, GetterTree } from 'vuex';
import { RootState } from '../../types/types';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  initiatePayment,
  verifyPayment,
  initiateMomoPayment,
  checkMomoPaymentStatus,
  creditTopUp,
  getAccountHolder,
} from '@/services/payments.service';
import { listPayments } from '../../services/payments.service';
import { AxiosResponse } from 'axios';

// Add MOMO payment status interface
interface IMomoPaymentStatus {
  status: string;
  message: string;
  data?: {
    referenceNo: string;
    amount: number;
    payer: string;
    paymentStatus: string;
  };
}

// Interface for credit top-up response
interface ICreditTopUpResponse {
  statusCode: string;
  statusMessage: string;
  transactionId: string;
  data: {
    totalAmount: number;
    amount: number;
    charge: number;
    referenceId: string;
  };
  links: Array<{
    rel: string;
    href: string;
  }>;
}

export const initialPaymentStatus = {
  title: 'Awaiting Payment Completion ...',
  subTitle: `Do not close this page or refresh the browser while payment is being processed.`,
  icon: require(`@/assets/images/hourglass.png`),
};

const state: BaseState<IPayments> = {
  list: [],
  invoices: [],
  paginate: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 15,
  },
  accountHolderDetails: {
    accountName: '',
    network: '',
    msisdn: '',
  },
  details: {
    channels: 'mobile_money',
    amount: 0,
    reference: '',
    transactionId: '',
    status: '',
    reason: '',
    id: 0,
  },
  resetForm: {
    channels: 'mobile_money',
    amount: 0,
    reference: '',
    transactionId: '',
    status: '',
    reason: '',
    id: 0,
  },
  // Add MOMO payment reference for tracking
  momoPaymentReference: '',
  currentPaymentStep: 'DETAILS',
  paymentStatusIndicatorResponse: initialPaymentStatus,
};

const mutations: MutationTree<BaseState<IPayments>> = {
  UPDATE_PAYMENTS(state, payload: IPayments[]) {
    state.list = payload;
  },
  UPDATE_PAYMENT_DETAILS(state, payload: IPayments) {
    state.details = payload;
  },
  UPDATE_CURRENT_PAYMENT_STEP(state, payload: string) {
    state.currentPaymentStep = payload;
  },
  UPDATE_ACCOUNT_HOLDER_DETAILS(state, payload: IAccountHolderDetails) {
    state.accountHolderDetails = payload;
  },
  UPDATE_PAYMENT_RESPONSE(state, payload: IPaymentStatusIndicatorResponse) {
    state.paymentStatusIndicatorResponse = payload;
  },
  UPDATE_PAGINATION(
    state,
    payload: Pick<IPaginate, 'total' | 'page' | 'itemsPerPage' | 'pages'>
  ) {
    state.paginate = {
      page: payload.page,
      total: payload.total,
      limit: payload.itemsPerPage,
      pages: payload.pages,
    };
  },
  // Add mutation to store MOMO payment reference
  UPDATE_MOMO_PAYMENT_REFERENCE(state, reference: string) {
    state.momoPaymentReference = reference;
  },
};

const actions: ActionTree<BaseState<IPayments>, RootState> = {
  async listPayments(
    { commit, dispatch },
    payload: IPaginate & { limit: number; query: string }
  ) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = of<IResponse<IPagination<IPayments>>>(
        await listPayments(payload.page, payload.limit, payload.query)
      );
      response$.subscribe(({ data }) => {
        const { page, limit, pages, total, docs } = data.paginateObj;
        dispatch('isPageLoading', false, { root: true });
        commit('UPDATE_PAYMENTS', docs);
        commit('UPDATE_PAGINATION', {
          page,
          pages,
          total,
          itemsPerPage: limit,
        });
        console.log(data);
      });
    } catch (e) {
      dispatch('isPageLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async accountHolderCheck({ dispatch, commit }, id: string) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<AxiosResponse<IResponse<IAccountHolderDetails>>>(
        await getAccountHolder(id)
      ).pipe(map((response) => response.data));
      response$.subscribe((response) => {
        dispatch('isLoading', false, { root: true });
        commit('UPDATE_CURRENT_PAYMENT_STEP', 'VERIFY_ACCOUNT');
        commit('UPDATE_ACCOUNT_HOLDER_DETAILS', response.data);
      });
    } catch (e) {
      dispatch('isPageLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.statusMessage, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async initiatePayment(
    { dispatch, commit },
    payload: Pick<IPayments, 'amount' | 'channels'> & { email: string }
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<AxiosResponse<IResponse<any>>>(
        await initiatePayment(payload)
      ).pipe(map((response) => response.data));
      response$.subscribe((response) => {
        commit('UPDATE_CURRENT_PAYMENT_STEP', 'PAYMENT_CONFIRMATION');
        dispatch('inProgress', true, { root: true });
        dispatch('isLoading', false, { root: true });
        dispatch('isDialogLoading', false, { root: true });
        dispatch('isPageLoading', false, { root: true });
        dispatch('resetFormValues', true, { root: true });
        dispatch(
          'updateDialog',
          { idx: 'payment', state: false },
          { root: true }
        );
      });
    } catch (e) {
      dispatch('isPageLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.statusMessage, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async verifyPayment({ commit, dispatch }, transactionId: string) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<
        AxiosResponse<
          IResponse<IKairosKashPaymentResponseData & { totalCredit: number }>
        >
      >(await verifyPayment(transactionId)).pipe(
        map((response) => response.data)
      );
      response$.subscribe((response) => {
        if (response.data.status === 'PENDING') {
          return;
        }
        dispatch('isLoading', false, { root: true });
        dispatch('inProgress', false, { root: true });
        const { status, reference, amount } = response.data;
        if (response.data.status === 'FAILED') {
          commit('UPDATE_PAYMENT_RESPONSE', {
            title: 'Payment Failed',
            subTitle:
              'Oops, it looks like your payment was unable to complete.Try again',
            icon: require('@/assets/images/attention.png'),
          } as IPaymentStatusIndicatorResponse);
        } else {
          commit('UPDATE_PAYMENT_RESPONSE', {
            title: 'Payment Completed',
            subTitle: `Your payment was processed successfully and your account will be credited with ${
              response.data.totalCredit
            } credit units.Thank you for choosing ${brandName()}.`,
            icon: require('@/assets/images/check.png'),
          } as IPaymentStatusIndicatorResponse);
          setTimeout(() => {
            dispatch('checkCreditBalance', null, { root: true });
            window.close();
          }, 2000);
          commit('UPDATE_CURRENT_PAYMENT_STEP', 'DETAILS');
        }
        commit('UPDATE_PAYMENT_DETAILS', {
          status,
          amount,
          reference,
          transactionId: reference,
        });
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // commit('UPDATE_PAYMENT_DETAILS', { reason: e.response.data?.message });
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
      dispatch('inProgress', false, { root: true });
    }
  },
};

const getters: GetterTree<BaseState<IPayments>, RootState> = {
  getPaymentList: (state) => state.list,
  getPagination: (state) => state.paginate,
  getPaymentResponse: (state) => state.details,
  getResetFormValues: (state) => state.resetForm,
  getAccountHolderDetails: (state) => state.accountHolderDetails,
  // Add getter for MOMO payment reference
  getMomoPaymentReference: (state) => state.momoPaymentReference,
  getCurrentPaymentStep: (state) => state.currentPaymentStep,
  getPaymentStatusIndicatorResponse: (state) =>
    state.paymentStatusIndicatorResponse,
};

export const payments: Module<BaseState<IPayments>, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
